import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';

export const useOperationsHead = () => {
  const queryClient = useQueryClient();

  // Get product categories
  const productCategories = useQuery({
    queryKey: ['productCategories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_categories')
        .select(`
          *,
          products(count)
        `)
        .order('va_from', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  // Get stone types for discount management
  const stoneTypes = useQuery({
    queryKey: ['stoneTypes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stone_rates')
        .select('*')
        .order('stone_type', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  // Get vendor change requests
  const vendorChangeRequests = useQuery({
    queryKey: ['vendorChangeRequests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_orders')
        .select(`
          *,
          customers(name, phone),
          products(model_name, category),
          vendors!sales_orders_vendor_id_fkey(name),
          requested_vendor:vendors!sales_orders_requested_vendor_id_fkey(name)
        `)
        .eq('status', 'vendor_change_requested')
        .order('change_requested_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Get discount proposals
  const discountProposals = useQuery({
    queryKey: ['discountProposals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('discount_proposals')
        .select(`
          *,
          proposed_by_user:users(name, role)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Create product category
  const createCategory = useMutation({
    mutationFn: async (categoryData: any) => {
      const { data, error } = await supabase
        .from('product_categories')
        .insert({
          ...categoryData,
          created_by: 'operations-head-id',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCategories'] });
    }
  });

  // Update category
  const updateCategory = useMutation({
    mutationFn: async ({ categoryId, updates }: { categoryId: string; updates: any }) => {
      const { data, error } = await supabase
        .from('product_categories')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', categoryId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCategories'] });
    }
  });

  // Assign products to category based on VA range
  const assignProductsToCategory = useMutation({
    mutationFn: async ({ categoryId, vaFrom, vaTo }: { 
      categoryId: string; 
      vaFrom: number; 
      vaTo: number; 
    }) => {
      const { data, error } = await supabase
        .from('products')
        .update({ 
          category_id: categoryId,
          updated_at: new Date().toISOString()
        })
        .gte('value_addition', vaFrom)
        .lte('value_addition', vaTo)
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productCategories'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  });

  // Update stone discounts
  const updateStoneDiscounts = useMutation({
    mutationFn: async ({ stoneId, discounts }: { stoneId: string; discounts: any }) => {
      const { data, error } = await supabase
        .from('stone_rates')
        .update({
          ...discounts,
          updated_by: 'operations-head-id',
          updated_at: new Date().toISOString()
        })
        .eq('id', stoneId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stoneTypes'] });
    }
  });

  // Approve vendor change
  const approveVendorChange = useMutation({
    mutationFn: async ({ orderId, approved, notes }: { 
      orderId: string; 
      approved: boolean;
      notes?: string;
    }) => {
      const updateData = approved 
        ? {
            status: 'vendor_changed',
            vendor_id: 'requested_vendor_id',
            vendor_change_approved: true,
            vendor_change_notes: notes,
            approved_by: 'operations-head-id',
            approved_at: new Date().toISOString()
          }
        : {
            status: 'forwarded_to_vendor',
            vendor_change_approved: false,
            vendor_change_notes: notes,
            rejected_by: 'operations-head-id',
            rejected_at: new Date().toISOString()
          };

      const { data, error } = await supabase
        .from('sales_orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorChangeRequests'] });
    }
  });

  // Propose discount/offer (to be approved by CEO)
  const proposeDiscount = useMutation({
    mutationFn: async (proposalData: any) => {
      const { data, error } = await supabase
        .from('discount_proposals')
        .insert({
          ...proposalData,
          proposed_by: 'operations-head-id',
          status: 'pending_ceo_approval',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discountProposals'] });
    }
  });

  return {
    productCategories,
    stoneTypes,
    vendorChangeRequests,
    discountProposals,
    createCategory,
    updateCategory,
    assignProductsToCategory,
    updateStoneDiscounts,
    approveVendorChange,
    proposeDiscount
  };
};